import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import useAuthStore from "../store/useAuthStore";
import {
  createProfile,
  deleteProfile,
  getProfileOptions,
  getProfiles,
  setWorkspaceProfile,
  setWorkspaceProfileForAll,
  updateProfile,
} from "../services/ProfileServices";

const initialFormState = {
  name: "",
  emailTemplateId: "",
  subscriberListId: "",
  whatsappTemplateName: "",
  isWorkspaceCampaign: false,
};

const Icon = ({ name, className = "h-5 w-5" }) => {
  const paths = {
    profile: (
      <>
        <rect x="3" y="4" width="18" height="16" rx="3" />
        <path d="M7 9h5M7 13h10M7 17h7" />
      </>
    ),
    building: (
      <>
        <path d="M4 21V5l8-3 8 3v16M8 8h1M15 8h1M8 12h1M15 12h1M8 16h1M15 16h1M10 21v-3h4v3" />
      </>
    ),
    lock: (
      <>
        <rect x="4" y="10" width="16" height="11" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </>
    ),
    users: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 20a6 6 0 0 1 12 0M16 5a3 3 0 0 1 0 6M17 14a5 5 0 0 1 4 5" />
      </>
    ),
    check: <path d="m5 12 4 4L19 6" />,
    edit: (
      <>
        <path d="m14 5 5 5M4 20l4.5-1 10-10a2 2 0 0 0-5-5l-10 10L4 20Z" />
        <path d="m12.5 5.5 5 5" />
      </>
    ),
    delete: (
      <>
        <path d="M4 7h16M9 7V4h6v3M6 7l1 14h10l1-14M10 11v6M14 11v6" />
      </>
    ),
    plus: <path d="M12 5v14M5 12h14" />,
    mail: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m4 7 8 6 8-6" />
      </>
    ),
    chevron: <path d="m9 18 6-6-6-6" />,
    close: <path d="m6 6 12 12M18 6 6 18" />,
  };

  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
};

const getSelectedOptionName = (items, selectedId) => {
  if (!selectedId) return "Not set";
  if (!items.length) return "Configured";
  return (
    items.find(
      (item) =>
        String(item.id) === String(selectedId) ||
        String(item.sourceId) === String(selectedId)
    )?.name ||
    "Unavailable"
  );
};

const getEmailTemplateSelectionValue = (items, selectedId) => {
  if (!selectedId) return "";
  return (
    items.find(
      (item) =>
        String(item.id) === String(selectedId) ||
        String(item.sourceId) === String(selectedId)
    )?.id || selectedId
  );
};

const getCampaignBadge = (profile) => {
  if (profile.isWorkspaceCampaign) {
    return {
      label: "Workspace",
      styles:
        "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20",
    };
  }

  return {
    label: profile.visibility === "PRIVATE" ? "Private" : "Admin only",
    styles:
      "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
  };
};

const CampaignCard = ({
  profile,
  options,
  subscriberOptions,
  onEdit,
  onDelete,
  deleteLoadingId,
}) => {
  const badge = getCampaignBadge(profile);
  const accessLabel = profile.isWorkspaceCampaign
    ? "Workspace"
    : profile.visibility === "PRIVATE"
      ? "Private"
      : "Admin only";

  return (
    <article
      className={`rounded-2xl border bg-white p-4 shadow-sm transition dark:bg-slate-900 sm:p-5 ${
        profile.isWorkspace
          ? "border-indigo-300 ring-2 ring-indigo-100 dark:border-indigo-500 dark:ring-indigo-500/15"
          : "border-slate-200 dark:border-slate-800"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-base font-bold text-slate-950 dark:text-white">
              {profile.name}
            </h3>
            {profile.isWorkspace && (
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
                <Icon name="check" className="h-3 w-3" /> Default
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Owned by {profile.user?.name || "Unknown"}
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ring-1 ${badge.styles}`}
        >
          {badge.label}
        </span>
      </div>

      <dl className="mt-4 grid gap-3 rounded-xl bg-slate-50 p-3 dark:bg-slate-950 sm:grid-cols-2">
        <div>
          <dt className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
            Email template
          </dt>
          <dd className="mt-1 truncate text-xs font-semibold text-slate-700 dark:text-slate-200">
            {getSelectedOptionName(
              options.emailTemplates,
              profile.emailTemplateId
            )}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
            Subscriber list
          </dt>
          <dd className="mt-1 truncate text-xs font-semibold text-slate-700 dark:text-slate-200">
            {getSelectedOptionName(subscriberOptions, profile.subscriberListId)}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
            WhatsApp template
          </dt>
          <dd className="mt-1 truncate text-xs font-semibold text-slate-700 dark:text-slate-200">
            {profile.whatsappTemplateName || "Not set"}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
            Access
          </dt>
          <dd className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200">
            <Icon
              name={profile.isWorkspaceCampaign ? "building" : "lock"}
              className="h-3.5 w-3.5"
            />
            {accessLabel}
          </dd>
        </div>
      </dl>

      {(profile.canEdit || profile.canDelete) && (
        <footer className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4 dark:border-slate-800">
          {profile.canEdit && (
            <button
              type="button"
              onClick={() => onEdit(profile)}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3.5 text-xs font-bold text-slate-700 transition hover:border-indigo-300 hover:text-indigo-700 dark:border-slate-700 dark:text-slate-200 dark:hover:border-indigo-600 dark:hover:text-indigo-300"
            >
              <Icon name="edit" className="h-4 w-4" /> Edit
            </button>
          )}
          {profile.canDelete && (
            <button
              type="button"
              disabled={deleteLoadingId === profile.id}
              onClick={() => onDelete(profile)}
              aria-label={`Delete ${profile.name}`}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-rose-200 px-3.5 text-xs font-bold text-rose-600 transition hover:bg-rose-50 disabled:opacity-50 dark:border-rose-900 dark:text-rose-300 dark:hover:bg-rose-950/40"
            >
              <Icon name="delete" className="h-4 w-4" />
              {deleteLoadingId === profile.id ? "Deleting…" : "Delete"}
            </button>
          )}
        </footer>
      )}
    </article>
  );
};

const CampaignSection = ({
  icon,
  title,
  description,
  profiles,
  emptyMessage,
  cardProps,
}) => (
  <section className="mt-6">
    <header className="mb-3 flex items-start gap-3">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
        <Icon name={icon} className="h-5 w-5" />
      </span>
      <div>
        <h2 className="text-lg font-bold text-slate-950 dark:text-white">
          {title}
        </h2>
        <p className="mt-0.5 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {description}
        </p>
      </div>
    </header>
    {profiles.length ? (
      <div className="grid gap-3 lg:grid-cols-2">
        {profiles.map((profile) => (
          <CampaignCard key={profile.id} profile={profile} {...cardProps} />
        ))}
      </div>
    ) : (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
        {emptyMessage}
      </div>
    )}
  </section>
);

const CampaignProfilesPage = () => {
  const { token, role } = useAuthStore((state) => state);
  const canConfigure = role === "admin" || role === "sub-admin";
  const [profiles, setProfiles] = useState([]);
  const [workspaceCampaignId, setWorkspaceCampaignId] = useState("");
  const [companyWideWorkspaceCampaignId, setCompanyWideWorkspaceCampaignId] =
    useState("");
  const [options, setOptions] = useState({
    emailTemplates: [],
    subscriberLists: [],
    subscriberSegments: [],
    whatsappTemplates: [],
  });
  const [formState, setFormState] = useState(initialFormState);
  const [editingId, setEditingId] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workspaceSaving, setWorkspaceSaving] = useState(false);
  const [applyingDefaultToAll, setApplyingDefaultToAll] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState("");
  const [optionWarnings, setOptionWarnings] = useState([]);

  const subscriberOptions = useMemo(
    () => [...options.subscriberLists, ...options.subscriberSegments],
    [options.subscriberLists, options.subscriberSegments]
  );
  const localEmailTemplates = useMemo(
    () => options.emailTemplates.filter((item) => item.source === "local"),
    [options.emailTemplates]
  );
  const pabblyEmailTemplates = useMemo(
    () => options.emailTemplates.filter((item) => item.source !== "local"),
    [options.emailTemplates]
  );
  const companyProfiles = profiles.filter(
    (profile) =>
      profile.visibility === "SHARED" &&
      (role === "admin" || profile.isWorkspaceCampaign)
  );
  const personalProfiles = profiles.filter(
    (profile) => profile.visibility === "PRIVATE" && profile.isOwner
  );
  const teamProfiles = profiles.filter(
    (profile) => profile.visibility === "PRIVATE" && !profile.isOwner
  );
  const availableProfiles = profiles.filter((profile) => profile.isSelectable);
  const activeCampaign = availableProfiles.find(
    (profile) => profile.id === workspaceCampaignId
  );
  const companyWideCampaign = profiles.find(
    (profile) => profile.id === companyWideWorkspaceCampaignId
  );
  const canApplyDefaultToAll = Boolean(
    role === "admin" &&
      activeCampaign?.visibility === "SHARED" &&
      activeCampaign?.isWorkspaceCampaign
  );

  const loadData = async () => {
    setLoading(true);
    try {
      const [profilesRes, optionsRes] = await Promise.all([
        getProfiles(),
        canConfigure ? getProfileOptions() : Promise.resolve(null),
      ]);
      setProfiles(profilesRes?.data?.profiles || []);
      setWorkspaceCampaignId(profilesRes?.data?.workspaceCampaignId || "");
      setCompanyWideWorkspaceCampaignId(
        profilesRes?.data?.companyWideWorkspaceCampaignId || ""
      );

      if (optionsRes) {
        setOptions({
          emailTemplates: optionsRes?.data?.emailTemplates || [],
          subscriberLists: optionsRes?.data?.subscriberLists || [],
          subscriberSegments: optionsRes?.data?.subscriberSegments || [],
          whatsappTemplates: optionsRes?.data?.whatsappTemplates || [],
        });
        setOptionWarnings(optionsRes?.data?.warnings || []);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Campaigns could not be loaded"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, role]);

  const closeForm = () => {
    setFormOpen(false);
    setEditingId("");
    setFormState(initialFormState);
  };

  const startCreate = () => {
    setEditingId("");
    setFormState(initialFormState);
    setFormOpen(true);
    requestAnimationFrame(() =>
      document
        .getElementById("campaign-form")
        ?.scrollIntoView({ behavior: "smooth", block: "start" })
    );
  };

  const startEdit = (profile) => {
    setEditingId(profile.id);
    setFormState({
      name: profile.name || "",
      emailTemplateId: getEmailTemplateSelectionValue(
        options.emailTemplates,
        profile.emailTemplateId
      ),
      subscriberListId: profile.subscriberListId || "",
      whatsappTemplateName: profile.whatsappTemplateName || "",
      isWorkspaceCampaign: Boolean(profile.isWorkspaceCampaign),
    });
    setFormOpen(true);
    requestAnimationFrame(() =>
      document
        .getElementById("campaign-form")
        ?.scrollIntoView({ behavior: "smooth", block: "start" })
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formState.name.trim() || !formState.emailTemplateId) {
      toast.error("Campaign name and email template are required");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await updateProfile(editingId, formState);
        toast.success("Campaign updated");
      } else {
        await createProfile(formState);
        toast.success(
          role === "admin"
            ? "Admin campaign created"
            : "Private campaign created"
        );
      }
      closeForm();
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Campaign could not be saved");
    } finally {
      setSaving(false);
    }
  };

  const handleWorkspaceChange = async (event) => {
    const profileId = event.target.value;
    const previousValue = workspaceCampaignId;
    setWorkspaceCampaignId(profileId);
    setWorkspaceSaving(true);
    try {
      await setWorkspaceProfile(profileId);
      toast.success(
        profileId ? "Default campaign updated" : "Default campaign cleared"
      );
      await loadData();
    } catch (error) {
      setWorkspaceCampaignId(previousValue);
      toast.error(
        error?.response?.data?.message || "Default campaign could not be updated"
      );
    } finally {
      setWorkspaceSaving(false);
    }
  };

  const handleSetDefaultForAll = async () => {
    if (!canApplyDefaultToAll) {
      toast.error("Choose a Workspace-marked campaign first");
      return;
    }

    if (
      !window.confirm(
        `Set "${activeCampaign.name}" as the default campaign for every other user? This will replace their current default campaign.`
      )
    ) {
      return;
    }

    setApplyingDefaultToAll(true);
    try {
      const response = await setWorkspaceProfileForAll(activeCampaign.id);
      setCompanyWideWorkspaceCampaignId(
        response?.data?.workspaceCampaignId || activeCampaign.id
      );
      toast.success(
        response?.data?.message || "Default campaign updated for other users"
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Default campaign could not be updated for other users"
      );
    } finally {
      setApplyingDefaultToAll(false);
    }
  };

  const handleDelete = async (profile) => {
    if (
      !window.confirm(
        `Permanently delete "${profile.name}"? This cannot be undone. Saved cards will keep the campaign name, but this campaign will no longer be available.`
      )
    ) {
      return;
    }

    setDeleteLoadingId(profile.id);
    try {
      await deleteProfile(profile.id);
      toast.success("Campaign deleted permanently");
      if (editingId === profile.id) closeForm();
      await loadData();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Campaign could not be deleted"
      );
    } finally {
      setDeleteLoadingId("");
    }
  };

  const cardProps = {
    options,
    subscriberOptions,
    onEdit: startEdit,
    onDelete: handleDelete,
    deleteLoadingId,
  };

  return (
    <main className="w-full px-4 py-5 sm:px-6 sm:py-8 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <header className="relative overflow-hidden rounded-3xl border border-indigo-100 bg-gradient-to-br from-white via-indigo-50/70 to-violet-100/70 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950/70 sm:px-8 sm:py-8">
          <div className="pointer-events-none absolute -right-16 -top-20 h-52 w-52 rounded-full bg-indigo-300/20 blur-3xl" />
          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-white">
                <Icon name="profile" className="h-4 w-4" /> Campaign workspace
              </span>
              <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-3xl">
                Campaign profiles
              </h1>
              <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600 dark:text-slate-400 sm:text-base">
                Choose the automation used when you save cards, and manage the
                campaigns your role allows.
              </p>
            </div>
            {canConfigure && (
              <button
                type="button"
                onClick={startCreate}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 text-sm font-bold text-white shadow-lg transition hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
              >
                <Icon name="plus" className="h-4 w-4" /> New campaign
              </button>
            )}
          </div>
        </header>

        <section className="mt-5 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:mt-6 sm:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                <Icon name="check" className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-base font-bold text-slate-950 dark:text-white">
                  Default campaign
                </h2>
                <p className="mt-1 max-w-lg text-xs leading-5 text-slate-500 dark:text-slate-400">
                  Several campaigns can be available to the workspace. Choose
                  which one should be preselected when you save a card.
                </p>
              </div>
            </div>
            <div className="w-full md:max-w-sm">
              <label htmlFor="workspace-campaign" className="sr-only">
                Default campaign
              </label>
              <div className="relative">
                <select
                  id="workspace-campaign"
                  value={workspaceCampaignId}
                  onChange={handleWorkspaceChange}
                  disabled={loading || workspaceSaving}
                  className="min-h-12 w-full appearance-none rounded-xl border border-slate-200 bg-white px-3.5 pr-10 text-sm font-semibold text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                >
                  <option value="">No default campaign</option>
                  {availableProfiles.some(
                    (profile) => profile.visibility === "SHARED"
                  ) && (
                    <optgroup label="Workspace campaigns">
                      {availableProfiles
                        .filter((profile) => profile.visibility === "SHARED")
                        .map((profile) => (
                          <option key={profile.id} value={profile.id}>
                            {profile.name}
                          </option>
                        ))}
                    </optgroup>
                  )}
                  {availableProfiles.some(
                    (profile) => profile.visibility === "PRIVATE"
                  ) && (
                    <optgroup label="My campaigns">
                      {availableProfiles
                        .filter((profile) => profile.visibility === "PRIVATE")
                        .map((profile) => (
                          <option key={profile.id} value={profile.id}>
                            {profile.name}
                          </option>
                        ))}
                    </optgroup>
                  )}
                </select>
                <Icon
                  name="chevron"
                  className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 rotate-90 text-slate-400"
                />
              </div>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                {workspaceSaving
                  ? "Saving selection…"
                  : activeCampaign
                    ? `${activeCampaign.name} will be preselected when you save a card.`
                    : "No campaign is currently preselected."}
              </p>
              {role === "admin" && (
                <div className="mt-3 rounded-xl border border-indigo-100 bg-indigo-50/70 p-3 dark:border-indigo-900/70 dark:bg-indigo-950/30">
                  <div className="mb-3 rounded-lg bg-white/80 px-3 py-2 dark:bg-slate-900/70">
                    <span className="block text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                      Default for all other users
                    </span>
                    <strong className="mt-1 block truncate text-sm text-indigo-800 dark:text-indigo-200">
                      {companyWideCampaign?.name || "Not set"}
                    </strong>
                  </div>
                  <button
                    type="button"
                    onClick={handleSetDefaultForAll}
                    disabled={
                      !canApplyDefaultToAll ||
                      workspaceSaving ||
                      applyingDefaultToAll
                    }
                    title={
                      canApplyDefaultToAll
                        ? "Replace every other user's current default campaign"
                        : "Select a campaign marked as a Workspace campaign"
                    }
                    className="inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-3 text-xs font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    <Icon name="users" className="h-4 w-4" />
                    {applyingDefaultToAll
                      ? "Applying to users…"
                      : "Set for all other users"}
                  </button>
                  <p className="mt-2 text-[11px] leading-4 text-indigo-700 dark:text-indigo-300">
                    This replaces each user&apos;s current default. Only a
                    campaign marked as Workspace can be applied.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {formOpen && canConfigure && (
          <section
            id="campaign-form"
            className="scroll-mt-24 mt-5 overflow-hidden rounded-3xl border border-indigo-200 bg-white shadow-xl shadow-indigo-100/40 dark:border-indigo-900 dark:bg-slate-900 dark:shadow-none sm:mt-6"
          >
            <header className="flex items-start justify-between gap-4 border-b border-slate-100 px-4 py-5 dark:border-slate-800 sm:px-6">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-indigo-600 dark:text-indigo-300">
                  {role === "admin" ? "Admin campaign" : "Private campaign"}
                </p>
                <h2 className="mt-1 text-xl font-bold text-slate-950 dark:text-white">
                  {editingId ? "Edit campaign" : "Create campaign"}
                </h2>
                <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                  {role === "admin"
                    ? "Mark it as a workspace campaign to make it available to other users."
                    : "This campaign will only be visible and usable by you."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeForm}
                aria-label="Close campaign form"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300"
              >
                <Icon name="close" className="h-5 w-5" />
              </button>
            </header>

            {optionWarnings.length > 0 && (
              <div
                role="status"
                className="mx-4 mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200 sm:mx-6"
              >
                <span className="font-bold">Some options could not be loaded.</span>{" "}
                Available templates can still be used. Please refresh once the
                connection is restored.
              </div>
            )}

            <form onSubmit={handleSubmit} className="p-4 sm:p-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                    Campaign name
                  </span>
                  <input
                    value={formState.name}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        name: event.target.value,
                      }))
                    }
                    placeholder="e.g. CPHI 2026 follow-up"
                    className="min-h-12 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-[15px] text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </label>

                <label className="block">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                    Email template
                  </span>
                  <select
                    value={formState.emailTemplateId}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        emailTemplateId: event.target.value,
                      }))
                    }
                    className="min-h-12 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  >
                    <option value="">Select email template</option>
                    {formState.emailTemplateId &&
                      !options.emailTemplates.some(
                        (item) => item.id === formState.emailTemplateId
                      ) && (
                        <option value={formState.emailTemplateId}>
                          Current selection (temporarily unavailable)
                        </option>
                      )}
                    {localEmailTemplates.length > 0 && (
                      <optgroup label="Email templates">
                        {localEmailTemplates.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {pabblyEmailTemplates.length > 0 && (
                      <optgroup label="Pabbly campaigns">
                        {pabblyEmailTemplates.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                    Subscriber list
                  </span>
                  <select
                    value={formState.subscriberListId}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        subscriberListId: event.target.value,
                      }))
                    }
                    className="min-h-12 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  >
                    <option value="">No subscriber list</option>
                    {subscriberOptions.map((item) => (
                      <option key={`${item.type || "list"}-${item.id}`} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                    WhatsApp template
                  </span>
                  <select
                    value={formState.whatsappTemplateName}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        whatsappTemplateName: event.target.value,
                      }))
                    }
                    className="min-h-12 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  >
                    <option value="">No WhatsApp template</option>
                    {options.whatsappTemplates.map((item) => (
                      <option key={item.id || item.name} value={item.name}>
                        {item.name}
                        {item.status ? ` (${item.status})` : ""}
                      </option>
                    ))}
                  </select>
                </label>

                {role === "admin" && (
                  <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-indigo-200 bg-indigo-50/70 p-4 transition hover:border-indigo-300 dark:border-indigo-900 dark:bg-indigo-950/30 sm:col-span-2">
                    <input
                      type="checkbox"
                      checked={formState.isWorkspaceCampaign}
                      onChange={(event) =>
                        setFormState((current) => ({
                          ...current,
                          isWorkspaceCampaign: event.target.checked,
                        }))
                      }
                      className="mt-0.5 h-5 w-5 shrink-0 accent-indigo-600"
                    />
                    <span>
                      <span className="block text-sm font-bold text-slate-900 dark:text-white">
                        Workspace campaign
                      </span>
                      <span className="mt-1 block text-xs leading-5 text-slate-600 dark:text-slate-400">
                        Make this campaign available for users and sub-admins to
                        choose as their default campaign.
                      </span>
                    </span>
                  </label>
                )}
              </div>

              <footer className="mt-6 flex flex-col-reverse gap-2 border-t border-slate-100 pt-5 dark:border-slate-800 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeForm}
                  className="min-h-12 rounded-xl border border-slate-200 px-5 text-sm font-bold text-slate-700 dark:border-slate-700 dark:text-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-6 text-sm font-bold text-white shadow-lg shadow-indigo-200 transition hover:bg-indigo-700 disabled:opacity-60 dark:shadow-indigo-950/40"
                >
                  <Icon name={editingId ? "check" : "plus"} className="h-4 w-4" />
                  {saving
                    ? "Saving…"
                    : editingId
                      ? "Save changes"
                      : "Create campaign"}
                </button>
              </footer>
            </form>
          </section>
        )}

        {loading ? (
          <div className="mt-6 grid gap-3 lg:grid-cols-2">
            {Array.from({ length: 4 }, (_, index) => (
              <div
                key={index}
                className="h-56 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800"
              />
            ))}
          </div>
        ) : (
          <>
            <CampaignSection
              icon="building"
              title="Admin campaigns"
              description="Campaigns marked Workspace are available to users and sub-admins."
              profiles={companyProfiles}
              emptyMessage="No admin campaigns are available yet."
              cardProps={cardProps}
            />

            {role === "sub-admin" && (
              <CampaignSection
                icon="lock"
                title="My campaigns"
                description="Private campaigns created and managed only by you."
                profiles={personalProfiles}
                emptyMessage="You have not created a private campaign yet."
                cardProps={cardProps}
              />
            )}

            {role === "admin" && (
              <CampaignSection
                icon="users"
                title="Team private campaigns"
                description="Private campaigns owned by sub-admins. Admins can review or permanently delete them."
                profiles={teamProfiles}
                emptyMessage="No private team campaigns have been created."
                cardProps={cardProps}
              />
            )}
          </>
        )}
      </div>
    </main>
  );
};

export default CampaignProfilesPage;
